import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useSelector } from 'react-redux';

import debounce from 'lodash.debounce';

import { selectApiConfig } from 'store/config/selectors';
import {
    selectChannels,
    selectGetChannelsRequestStatus,
} from 'store/programs/selectors';
import {
    MdsCallKeys,
    RBBottomTabMenuItem,
    RBParsedRail,
} from 'store/railsBuilder/reducer.types';

import useCollectionDataManager from 'services/data-manager/hooks/useCollectionDataMananger';
import parseKidsChannels from 'services/data-manager/parse-kids-channels';
import {
    ContentType,
    EventItemWithServiceInfo,
} from 'services/data-manager/types';

import { CollectionItemsList } from 'screens/Collection/types';
import {
    OnSetStripeLoadingStatus,
    RailLoadingStatus,
} from 'screens/Modular/types';

import useMdsStripeLoadingStatus from 'components/Stripe/hooks/useMdsStripeLoadingStatus';

type Params = {
    rail: RBParsedRail;
    templateId: RBBottomTabMenuItem['id'];
    loadingStatus: RailLoadingStatus;
    onSetStripeLoadingStatus: OnSetStripeLoadingStatus;
};

type ReturnType = {
    hasNext: boolean;
    data: CollectionItemsList;
    nextUpdateTime?: undefined | number;
    onReloadStripeData?(): void;
};

function useStripeDataLoading({
    rail,
    templateId,
    loadingStatus,
    onSetStripeLoadingStatus,
}: Params) {
    const {
        properties: { MDS_Call, limit, ContentType: type },
    } = rail;
    const { default_page_size = 20 } = useSelector(selectApiConfig);
    const channels = useSelector(selectChannels);
    const channelsRequestStatus = useSelector(selectGetChannelsRequestStatus);

    const { data, hasNext, nextUpdateTime, onGetCollectionData } =
        useCollectionDataManager({
            templateId,
            rail,
            channels,
            limit: rail.properties.limit
                ? parseInt(rail.properties.limit, 10)
                : default_page_size,
            onSetStripeLoadingStatus,
        });

    const handleDebouncedFetch = useRef(
        debounce(onGetCollectionData, 300, {
            leading: true,
            trailing: false,
        }),
    );

    useMdsStripeLoadingStatus({
        stripeId: rail.id,
        mdsCall: MDS_Call,
        requestStatus: channelsRequestStatus,
        responseData: channels,
        onSetStripeLoadingStatus,
    });

    const onReloadStripeData = useCallback(() => {
        if (
            (!MDS_Call ||
                MDS_Call === MdsCallKeys.RECORDINGS ||
                MDS_Call === MdsCallKeys.NOW_NEXT) &&
            (loadingStatus === RailLoadingStatus.LOADED ||
                loadingStatus === RailLoadingStatus.EMPTY)
        ) {
            onGetCollectionData(true);
        }
    }, [MDS_Call, loadingStatus, onGetCollectionData]);

    useEffect(() => {
        // Fetch rail data for the rails
        // without "MDS_Call" property defined or if "MDS_Call" property points to RECORDINGS rail.
        // Otherwise - implement fetching logic based on the "MDS_Call" propery value.
        if (
            (!MDS_Call ||
                MDS_Call === MdsCallKeys.RECORDINGS ||
                MDS_Call === MdsCallKeys.NOW_NEXT) &&
            !data?.length &&
            loadingStatus === RailLoadingStatus.TO_LOAD
        ) {
            handleDebouncedFetch.current();
        }
    }, [data?.length, loadingStatus]);

    // if rail.properties.MDS_Call is not defined - app will use /templateviews/rails/${railID} to get rail data.
    // if rail.properties.MDS_Call will be RECORDINGS - app will need to retrieve rail(stripe) data using recordings API.
    // if rail.properties.MDS_Call will be defined - app will need to retrieve rail(stripe) data accordingly using different API.
    // in existing state of app MDS_Call data is now-next and channels which are loaded on app start and used from redux store.
    return useMemo<ReturnType>(() => {
        const stripeLimit =
            Number(limit) && !Number.isNaN(Number(limit))
                ? Number(limit)
                : default_page_size;

        if (MDS_Call && MDS_Call === MdsCallKeys.CHANNELS) {
            return {
                hasNext: stripeLimit < channels.length,
                data: channels.slice(0, stripeLimit),
            };
        }

        // Kids Channels stripe will be built based on the /rails/${id} response data
        if (!MDS_Call && type === ContentType.ChannelItem) {
            const channelsRailItems = parseKidsChannels(
                channels,
                data as EventItemWithServiceInfo[],
            );

            return {
                hasNext: stripeLimit < channelsRailItems.length,
                data: channelsRailItems,
            };
        }

        return {
            hasNext,
            data,
            nextUpdateTime,
            onReloadStripeData,
        };
    }, [
        limit,
        default_page_size,
        MDS_Call,
        type,
        hasNext,
        data,
        nextUpdateTime,
        onReloadStripeData,
        channels,
    ]);
}

export default useStripeDataLoading;
